/**
 * Actions kebab menu for the sticky top bar (design §11) + the shared menu
 * panel anatomy (panel class, pastel rows, the in-panel × close) reused by
 * downloads/DownloadMenu.tsx. Item VISIBILITY comes exclusively from the
 * `can.*` flags + derived-status action map computed by the header.
 */
import {
	CircleEllipsis,
	CreditCard,
	FileOutput,
	FilePlus2,
	Landmark,
	Pencil,
	RefreshCw,
	RotateCcw,
	Send,
	SendHorizontal,
	ShieldCheck,
	Trash2,
	UserPlus,
	X,
} from 'lucide-react'
import type { ReactNode } from 'react'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '#app/components/ui/dropdown-menu'
import type { ReportActionMode } from '#app/utils/application-detail-v2/derive'
import { IconSquare, type PastelTone } from './primitives'

/** Compact panel (user feedback 2026-06-13 ×2: the design frames' oversized
 * menus overwhelmed the real top bar — both menus share this density). */
export const MENU_PANEL_CLASS =
	'relative rounded-2xl bg-[#FBFDFD] p-3.5 shadow-[0px_0px_16px_0px_#0000000A,0px_0px_4px_0px_#0000000A]'

/** Design §9/§11: the open panels carry their own small × close icon. */
export function CloseItem() {
	return (
		<DropdownMenuItem className="absolute top-3 right-3 cursor-pointer rounded-full p-1 focus:bg-[#F6F7F5]">
			<X className="size-3.5 text-[#32302F]" />
			<span className="sr-only">Close menu</span>
		</DropdownMenuItem>
	)
}

export function MenuRow({
	tone = 'neutral',
	icon,
	iconSrc,
	label,
	onSelect,
}: {
	tone?: PastelTone
	icon?: ReactNode
	/** Brand SVG carrying its own pastel tile (public/img) — replaces the
	 * tone + lucide glyph square, like SectionIcon does for section headers. */
	iconSrc?: string
	label: string
	onSelect?: () => void
}) {
	return (
		<DropdownMenuItem
			onSelect={onSelect}
			className="flex cursor-pointer items-center gap-3 rounded-lg px-2 py-2 focus:bg-[#F6F7F5]"
		>
			{iconSrc ? (
				<img
					src={iconSrc}
					alt=""
					aria-hidden="true"
					className="size-9 shrink-0 rounded-lg"
				/>
			) : (
				<IconSquare tone={tone} className="size-9 rounded-lg [&>svg]:size-4">
					{icon}
				</IconSquare>
			)}
			<span className="text-[15px] font-Regular text-[#32302F]">{label}</span>
		</DropdownMenuItem>
	)
}

export interface KebabItems {
	/** "Add Applicant" — invite a co-applicant into THIS application's
	 * household (the combined page's "+ Add Applicant", surfaced here too).
	 * Shown for solo applications as well as grouped ones: on a solo entry the
	 * invite CREATES the groupId, which is how a single application grows into
	 * a household from this page. */
	addApplicant: boolean
	requestGuarantor: boolean
	requestBankAccount: boolean
	/** "Request Documents" — ask selected members for specific documents
	 * (client 2026-08-27); absent on surfaces that don't offer it. */
	requestDocuments?: boolean
	/** Navigates to the property edit page (properties.manage). */
	editProperty?: boolean
	/** Opens the dashboard's Reach Out dialog (call/text a member). */
	reachOut?: boolean
	/** "Send Update Link" — emails the applicant a link to revisit/resume
	 * their screening (the v2 `sendUpdateLink` intent; the old fail-closed
	 * `share` intent was removed). */
	sendUpdateLink: boolean
	withdraw: boolean
	/** Excel: reinstate withdrawn application. */
	reinstate: boolean
	/** Report (re)generation entry, or null to hide (DETAIL-V2-DECISION-RULES.md
	 * rule 3, derived by {@link deriveReportAction}): `'regenerate'` for a
	 * finished+undecided report, `'generate'` for a FAILED one (no report to
	 * replace). The verb drives the menu label. */
	reportAction: ReportActionMode | null
	/** Soft-delete this application (`applications.delete`); individual page
	 * only — the combined/household view leaves this false. */
	deleteApplication: boolean
}

export function KebabMenu({
	items,
	onAddApplicant,
	onRequestGuarantor,
	onRequestBankAccount,
	onRequestDocuments,
	onEditProperty,
	onReachOut,
	onSendUpdateLink,
	onWithdraw,
	onReinstate,
	onRegenerateReport,
	onDeleteApplication,
}: {
	items: KebabItems
	onAddApplicant?: () => void
	onRequestGuarantor?: () => void
	onRequestBankAccount?: () => void
	onRequestDocuments?: () => void
	onEditProperty?: () => void
	onReachOut?: () => void
	onSendUpdateLink?: () => void
	onWithdraw?: () => void
	onReinstate?: () => void
	onRegenerateReport?: () => void
	onDeleteApplication?: () => void
}) {
	return (
		<DropdownMenu>
			<DropdownMenuTrigger
				aria-label="More actions"
				className="flex size-11 cursor-pointer items-center justify-center rounded-full bg-[#FBFDFD] text-[#32302F] transition-colors hover:bg-[#F6F7F5]"
			>
				{/* Design §2: a dark circled-ellipsis glyph inside the white circle. */}
				<CircleEllipsis className="size-6" />
			</DropdownMenuTrigger>
			<DropdownMenuContent
				align="end"
				sideOffset={10}
				className={`${MENU_PANEL_CLASS} w-[260px]`}
			>
				<div className="mb-[17px]">
					<CloseItem />
				</div>
				{items.requestGuarantor ? (
					<MenuRow
						tone="mint"
						icon={<ShieldCheck />}
						label="Request Guarantor"
						onSelect={onRequestGuarantor}
					/>
				) : null}
				{items.requestBankAccount ? (
					<MenuRow
						tone="blue"
						icon={<Landmark />}
						label="Request Bank Account"
						onSelect={onRequestBankAccount}
					/>
				) : null}
				{items.requestDocuments ? (
					<MenuRow
						tone="sand"
						icon={<FilePlus2 />}
						label="Request Documents"
						onSelect={onRequestDocuments}
					/>
				) : null}
				{items.addApplicant ? (
					<MenuRow
						tone="purple"
						icon={<UserPlus />}
						label="Add Applicant"
						onSelect={onAddApplicant}
					/>
				) : null}
				{items.editProperty ? (
					<MenuRow
						tone="blush"
						icon={<Pencil />}
						label="Edit Property"
						onSelect={onEditProperty}
					/>
				) : null}
				{items.reportAction ? (
					// `green` is already the requested #D6E5BE40 / #A5BB83 pair.
					<MenuRow
						tone="green"
						icon={<RefreshCw />}
						label={
							items.reportAction === 'generate'
								? 'Generate Report'
								: 'Regenerate Report'
						}
						onSelect={onRegenerateReport}
					/>
				) : null}
				{items.reachOut ? (
					<MenuRow
						tone="lpurple"
						icon={<Send />}
						label="Reach Out"
						onSelect={onReachOut}
					/>
				) : null}
				{items.sendUpdateLink ? (
					<MenuRow
						tone="purple"
						icon={<SendHorizontal />}
						label="Share"
						onSelect={onSendUpdateLink}
					/>
				) : null}
				{items.withdraw ? (
					<MenuRow
						tone="lrose"
						icon={<FileOutput />}
						label="Withdraw"
						onSelect={onWithdraw}
					/>
				) : null}
				{items.reinstate ? (
					<MenuRow
						tone="lpurple"
						icon={<RotateCcw />}
						label="Reinstate"
						onSelect={onReinstate}
					/>
				) : null}
				{items.deleteApplication ? (
					<MenuRow
						tone="lred"
						icon={<Trash2 />}
						label="Delete"
						onSelect={onDeleteApplication}
					/>
				) : null}
			</DropdownMenuContent>
		</DropdownMenu>
	)
}
